import { Vue, prop, Options } from 'vue-class-component'
import apiService from '@/services/api-service'
import { Config } from '@/config'
import { IPaginationParam } from '@/models/common'
import utilityService from '@/services/utility-service'
import PersonAdvanceSearch from '../PersonAdvanceSearch/PersonAdvanceSearch.vue'
import CompanyAdvanceSearch from '../CompanyAdvanceSearch/CompanyAdvanceSearch.vue'
import { getSearchResultsParams, getSingleSearchResults, resetErrorData } from '@/models/search'

// Define props in a class
class Props {
  page = prop({
    // Same as Vue core's prop option
    type: String,
    required: false,
    default: 'home'
  });
}

const MAX_FIELD_LENGTH = 200
const DEFAULT_SEARCH_DELAY_MS = 400

@Options({
  components: { PersonAdvanceSearch, CompanyAdvanceSearch }
})
export default class Search extends Vue.with(Props) {
  Config = Config
  public searchResult: any[] = [];
  public searchingCompleted = false;
  isFormValid = true;
  formError: any = {};
  paginationSubscription: any;
  searchType: any = { person: true, company: true };
  activeTab = Config.CONSTANTS.PERSON;
  openAdvanceSearch = false;
  basicSearchForm: any = { name: '', address: '' };

  defaultPaginationParams: IPaginationParam = {
    page: 1,
    page_size: apiService.result.person.perPageCount,
    form: Config.CONSTANTS.PERSON
  }

  mounted () {
    const query = this.$route.query

    this.basicSearchForm.name = query.person_name || query.company_name || ''
    this.basicSearchForm.address = query.address || ''
    this.updatePageTitleFromSearchForm()

    if (query.company === 'false') {
      this.searchType.company = false
    }

    if (query.person === 'false') {
      this.searchType.person = false
    }

    utilityService.bus.on('OPEN_ADVANCE_SEARCH', this.openAdvanceSearchRequestHandler)
  }

  beforeDestroy () {
    utilityService.bus.off('OPEN_ADVANCE_SEARCH', this.openAdvanceSearchRequestHandler)
  }

  created () {
    if (!apiService.isInitialSearchDone) {
      apiService.onSearchOptionChanged.next(this.searchType)
    }
  }

  openAdvanceSearchRequestHandler () {
    this.openAdvanceSearch = true
  }

  updatePageTitleFromSearchForm () {
    const titleParams: string[] = []

    if (this.basicSearchForm.name) {
      titleParams.push(this.basicSearchForm.name)
    }
    if (this.basicSearchForm.address) {
      titleParams.push(this.basicSearchForm.address)
    }

    if (titleParams.length > 0) {
      utilityService.updatePageTitle(titleParams.join(' - '))
    }
  }

  waitForNext (delay: number = DEFAULT_SEARCH_DELAY_MS) {
    return new Promise<void>((resolve) => {
      setTimeout(() => {
        resolve()
      }, delay)
    })
  }

  public onBasicSearch () {
    this.hideToast()
    if (this.validateForm(this.basicSearchForm)) {
      apiService.resetResult()
      apiService.onQuickFilterChangedSubject.next({
        reset: true,
        form: '',
        doSearch: false
      })
      Object.assign(this.basicSearchForm, {
        ...(this.searchType.company ? { company: true } : { company: false }),
        ...(this.searchType.person ? { person: true } : { person: false })
      })
      const params = getSearchResultsParams(
        this.$route.query,
        this.basicSearchForm,
        this.defaultPaginationParams,
        this.getSearchType(),
        true
      )

      this.$router
        .push({
          name: 'Result',
          query: params
        })
        .then(async () => {
          if (this.searchType.person) {
            apiService.emitNewResult(Config.CONSTANTS.COMPANY, true)
            await this.waitForNext()
            apiService.emitNewResult(Config.CONSTANTS.PERSON, true)
          } else {
            apiService.emitNewResult(Config.CONSTANTS.PERSON, true)
            await this.waitForNext()
            apiService.emitNewResult(Config.CONSTANTS.COMPANY, true)
          }
        })
    }
  }

  public validateForm (formObj: any) {
    this.hideToast()
    this.resetErrors()
    if (!this.validateNameAndAddress(formObj)) {
      return
    }
    for (const [key] of Object.entries(formObj)) {
      this.validateFieldsLength(formObj, key, false)
      this.validateFieldForSpecialChar(formObj, key)
    }
    return this.isFormValid
  }

  validateFieldsLength (formObj: any, field: string, isRequired: boolean) {
    if (isRequired || (!isRequired && formObj[field] && formObj[field].length > 0)) {
      const valueLength = formObj[field].length
      if (valueLength > MAX_FIELD_LENGTH) {
        this.onFieldValidationFailed(
          field,
          this.$t('default.form_error.max_char_count', {
            field: this.getPlaceholderKeyByField(field)
          })
        )
      }
    }
  }

  getPlaceholderKeyByField (field: string) {
    return this.$t('default.search_form.' + field)
  }

  validateFieldForSpecialChar (formObj: any, field: string) {
    // Special characters validation
    /* eslint-disable */
    const value = formObj[field];
    if (typeof value !== 'string') return;
    const pattern = /['!@#$%^&*()+\=\[\]{};:"\\|<>\/?~]/;
    if (value?.match(pattern)) {
      this.onFieldValidationFailed(
        field,
        this.$t("default.form_error.no_special_char", {
          field: this.getPlaceholderKeyByField(field)
        })
      );
    }
  }

  validateNameAndAddress (formObj: any) {
    if (formObj.name || formObj.address) {
      return true
    } else {
      this.onNameOrAddressValidationFailed()
      return false
    }
  }

  onNameOrAddressValidationFailed () {
    this.formError.name = true
    this.formError.address = true
    this.isFormValid = false
    utilityService.showToast(
      this.$t('default.form_error.name_or_address'), 
      'error', 
      'form_error'
    )
  }

  resetErrors() {
    this.isFormValid = true;
    this.formError = resetErrorData
    this.hideToast();
  }

  onFieldValidationFailed (field: string, message: string) {
    this.formError[field] = true
    this.isFormValid = false
    utilityService.showToast(message, 'error', 'form_error')
  }

  canShowFormError (control: string) {
    return this.formError[control]
  }

  getSearchType (): string {
    if (this.searchType.person && this.searchType.company) {
      return 'all'
    } else if (this.searchType.person) {
      return Config.CONSTANTS.PERSON
    } else if (this.searchType.company) {
      return Config.CONSTANTS.COMPANY
    } else {
      return 'all'
    }
  }

  public hideNoResultMsg () {
    this.hideToast()
    this.searchingCompleted = false
  }

  public isCompactForm () {
    return this.page === 'result'
  }

  public canShowNoResult () {
    return (
      this.searchingCompleted &&
      this.searchResult.length === 0 &&
      this.page === 'home'
    )
  }

  hideToast () {
    utilityService.hideToast()
  }

  resetField(field: 'name' | 'address') {
    this.basicSearchForm[field] = ''
    this.formError.search = false
  }

  onSearchToggle (type: string) {
    this.searchType[type] = !this.searchType[type]
    if (type === Config.CONSTANTS.PERSON && !this.searchType.person) {
      this.searchType.company = true
      this.activeTab = Config.CONSTANTS.COMPANY
    } else if (type === Config.CONSTANTS.COMPANY && !this.searchType.company) {
      this.searchType.person = true
      this.activeTab = Config.CONSTANTS.PERSON
    }
    apiService.onSearchOptionChanged.next(this.searchType)
    let params: any = Object.assign({}, this.$route.query)
    if (!params) {
      params = {
        company: this.searchType.company,
        person: this.searchType.person
      }
    } else {
      params.company = this.searchType.company
      params.person = this.searchType.person
    }
    params.search_type = this.activeTab
    if (this.isCompactForm()) {
      this.gotoResultPage(params)
    }
  }

  gotoResultPage (params: any) {
    this.$router.push({
      name: 'Result',
      query: params
    })
  }

  getPlaceholderForName () {
    return (
      this.$t('default.search_form.name')
    )
  }

  getPlaceholderForPlace () {
    return (
      this.$t('default.search_form.address')
    )
  }

  toggleAdvanceSearchPopup () {
    this.openAdvanceSearch = !this.openAdvanceSearch
  }
}
